import * as React from "react";
import { Globe } from "lucide-react";
import { zoneOffset } from "@/lib/dates";
import { FilterSelect } from "./FilterSelect";
import type { FilterOption } from "./FilterSelect";

/**
 * An IANA timezone picker. Emits and accepts a plain zone id - `America/New_York`
 * - which is exactly what `sites.timezone` and `users.timezone` already hold, so
 * nothing about storage changes.
 *
 * ── Why this is a `FilterSelect` and not `react-timezone-select` ────────────
 *
 * The reference product (`visionai-app/ui/src/components/TimezoneField.tsx`)
 * wraps `react-timezone-select` over `react-select`, and it pays for it twice:
 * two dependencies for one control, and ~70 lines of JS style objects that
 * restate the design system in a second, unenforced place - the file's own
 * comment admits the previous version drifted to a different height, a different
 * font size and no focus ring. This console already has a searchable dropdown
 * with a second line per option and group headings, which is the whole feature
 * set, and it inherits the field styling by being the same component as every
 * other picker in a form.
 *
 * ── What the reader actually needs to see ───────────────────────────────────
 *
 * A bare `America/New_York` is not a choosable label: somebody in Texas has to
 * know that their zone is spelled `Chicago`. So the option carries the city on
 * the first line, the region as a group heading, and the current UTC offset plus
 * the canonical id on the second line. `FilterSelect` searches the hint as well
 * as the label, which is what makes "+05:30", "Kolkata" and a pasted
 * `Asia/Calcutta` all find the same row.
 *
 * ── The offsets are computed once, on first use ─────────────────────────────
 *
 * There are roughly 420 zones and each needs two `Intl.DateTimeFormat`
 * instances to describe. Building them per render made opening the picker
 * visibly slow, so the list is memoised at module scope behind a lazy getter.
 *
 * The consequence is that offsets are correct as of the first time a picker is
 * opened in this tab, not as of every render. That is a page-lifetime staleness
 * across a DST boundary, on a hint - not on the value, which is the zone id and
 * does not move.
 */

/** What the browser thinks it is in. The default for anything created here. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * `Intl.supportedValuesOf` is ES2022 and not in this project's TS lib, and it is
 * genuinely absent on older Safari. The fallback is the browser's own zone plus
 * UTC - a two-option picker rather than a broken one, and the field is still
 * satisfiable because every user has a valid zone to keep.
 */
function supportedZones(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  try {
    const zones = intl.supportedValuesOf?.("timeZone");
    if (zones && zones.length > 0) return zones;
  } catch {
    /* falls through */
  }
  return Array.from(new Set(["UTC", browserTimezone()]));
}

interface ZoneOption extends FilterOption {
  value: string;
}

function buildOptions(): ZoneOption[] {
  const now = new Date();

  const rows = supportedZones().map((zone) => {
    const slash = zone.indexOf("/");
    // `UTC` and `GMT` have no region. They are common enough to deserve a
    // heading of their own rather than being filed under "Other".
    const region = slash === -1 ? "UTC" : zone.slice(0, slash);
    const city = slash === -1 ? zone : zone.slice(slash + 1);

    return {
      value: zone,
      label: city.replace(/_/g, " ").replace(/\//g, " / "),
      // The canonical id is on screen deliberately. This is a console where
      // somebody may be reconciling a zone against an export or a ticket, and
      // it is also what makes a pasted `Asia/Calcutta` findable.
      hint: `${zoneOffset(zone, now)} · ${zone}`,
      group: region,
    };
  });

  return rows.sort(
    (a, b) => (a.group ?? "").localeCompare(b.group ?? "") || a.label.localeCompare(b.label)
  );
}

let cached: ZoneOption[] | null = null;

function allZoneOptions(): ZoneOption[] {
  if (!cached) cached = buildOptions();
  return cached;
}

export function TimezoneSelect({
  value,
  onChange,
  id,
  label = "Timezone",
  disabled,
  invalid,
}: {
  value: string;
  onChange: (zone: string) => void;
  id?: string;
  /** The accessible name. The trigger only ever shows the chosen zone. */
  label?: string;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const options = React.useMemo(() => {
    const all = allZoneOptions();
    const detected = browserTimezone();

    /**
     * The browser's zone, pinned to the top under its own heading.
     *
     * It is right for almost everybody creating their own depot or editing their
     * own profile, and it used to be the *only* behaviour here - the field was a
     * text input seeded from `Intl` that nobody could sensibly change. Keeping it
     * one click away is the half of that which was worth keeping.
     *
     * A stored zone the runtime does not know about - an old row, a hand-typed
     * value from when this was a free-text box - is pinned beside it. Dropping it
     * would render the trigger's placeholder over a value that is really there,
     * and the first save would silently rewrite it.
     */
    const pinned: ZoneOption[] = [];
    const seen = new Set<string>();

    for (const zone of [detected, value]) {
      if (!zone || seen.has(zone)) continue;
      seen.add(zone);
      const known = all.find((option) => option.value === zone);
      pinned.push({
        ...(known ?? { value: zone, label: zone.replace(/_/g, " "), hint: zone }),
        value: zone,
        group: zone === detected ? "Detected here" : "Current setting",
      });
    }

    // Pinned zones are *moved*, not copied. `FilterSelect` keys its options on
    // the value, so leaving the original row in place would duplicate a React
    // key - and would put the same city on screen twice for no reason.
    return [...pinned, ...all.filter((option) => !seen.has(option.value))];
  }, [value]);

  return (
    <FilterSelect
      id={id}
      variant="field"
      label={label}
      icon={<Globe />}
      placeholder="Select a timezone"
      searchPlaceholder="Search city, offset or zone id"
      emptyLabel="No timezone matches that"
      searchable
      value={value}
      options={options}
      onChange={onChange}
      disabled={disabled}
      invalid={invalid}
    />
  );
}
